import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Typography,
  Link,
  Stack,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import { useState } from "react";
import history from "utils/history";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { verifyTOTP } from "screens/Auth/AuthService";
import { MuiOtpInput } from "mui-one-time-password-input";
import {
  convertResourceToObjectFormat,
  isTruthy,
  isValidOTP,
  openErrorNotification,
} from "helpers/methods";
import { loginAction, selectAuthenticated } from "redux/authSlice";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAuthRouteRedirection } from "layouts/Config";
import viewpaths from "global/constants/ViewPathConstants";
import defaultImage from "assets/icons1/avatars/default.png";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import authStyles from "./Auth.styles";
import pandaIcon from "assets/icons1/panda_icon.svg";
import mobilePandaIcon from "assets/icons1/mobile_panda_icon.svg";
import { updatePreference } from "redux/preferencesSlice";

interface CustomProps {
  location: any;
}

const OTP = (props: CustomProps) => {
  useTitle(title.VERITY_OTP);
  const dispatch = useAppDispatch();
  const classes = authStyles;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const email = props.location.state?.email ?? "";
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [payload, setPayload] = useState({
    email: email,
    otp: "",
  });
  const [processing, setProcessing] = useState<boolean>(false);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const verifyOTP = async () => {
    try {
      if (!isValidOTP(payload.otp)) {
        return openErrorNotification("Please enter the code");
      }
      setProcessing(true);
      const response = await verifyTOTP(payload.email, parseInt(payload.otp));
      const formattedResources = convertResourceToObjectFormat(
        response.resources
      );
      if (!isTruthy(formattedResources)) {
        openErrorNotification("Something went wrong!");
        setProcessing(false);
        return;
      }
      dispatch(
        loginAction({
          authenticated: true,
          accessToken: response.authToken,
          refreshToken: response.refreshToken,
          userName: response.name,
          email: (response.email ?? "").toLowerCase(),
          userRole: response.role,
          companyId: response.companyId,
          resources: formattedResources,
          profileImage: response.profilePicture
            ? "data:image/png;base64," + response.profilePicture
            : defaultImage,
          account: response.account,
          userId: response.id,
          isTrial: response.trial,
        })
      );
      dispatch(
        updatePreference({
          layout: "vertical",
          theme: "dark",
        })
      );
      setProcessing(false);
    } catch (error: any) {
      setProcessing(false);
      openErrorNotification(error.message);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verifyOTP();
    }
  };

  const renderOTPScreen = () => {
    return (
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={mdUp ? pandaIcon : mobilePandaIcon}
          alt="Panda"
          sx={{
            position: "absolute",
            width: mdUp ? "150px" : "100px",
            height: "auto",
            right: mdUp ? "-86px" : "50%",
            left: mdUp ? "auto" : "50%",
            top: mdUp ? "50%" : "-70px",
            transform: mdUp ? "translateY(-50%)" : "translateX(-50%)",
            zIndex: 2,
          }}
        />
        <Card elevation={16} sx={classes.cardBox}>
          <CardHeader
            subheader={
              <Typography
                color="text.primary"
                variant="body2"
                textAlign={"center"}
              >
                Please enter the authenticator code to securely login.
              </Typography>
            }
            sx={{ pb: 0 }}
            title={
              <Typography
                color="text.primary"
                variant="h4"
                textAlign={"center"}
                gutterBottom
              >
                Enter Authenticator Code
              </Typography>
            }
          />
          <CardContent
            sx={{
              py: 1,
              px: 3,
            }}
          >
            <Stack spacing={2}>
              <FormControl sx={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <MuiOtpInput
                  length={6}
                  onChange={(value: any) =>
                    setPayload({
                      ...payload,
                      otp: value.replace(/[^0-9]/g, ""),
                    })
                  }
                  sx={{
                    "& .MuiFilledInput-input": {
                      p: isDesktop ? "14px" : "10px",
                    },
                    gap: isDesktop ? "20px" : "8px",
                    "& .MuiFilledInput-root": {
                      width: isDesktop ? "auto" : "32px",
                      minWidth: isDesktop ? "auto" : "32px",
                    },
                  }}
                  value={payload.otp}
                  onKeyPress={handleKeypress}
                  TextFieldsProps={{
                    type: "number",
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: {
                        MozAppearance: "textfield",
                      },
                    },
                    sx: {
                      "& input[type=number]": {
                        MozAppearance: "textfield", 
                      },
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                    },
                  }}
                />
              </FormControl>
              <Button
                fullWidth
                size="large"
                sx={{ mt: 2 }}
                type="submit"
                variant="contained"
                onClick={verifyOTP}
                disabled={processing}
              >
                Verify
              </Button>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <Link
                  underline="hover"
                  variant="subtitle2"
                  onClick={() => history.push(viewpaths.loginViewPath)}
                >
                  Back to Login
                </Link>
              </Box>
              <Typography variant="body2" color="text.primary">
                If you're unable to login, please reach out to admin to reset
                the multi-factor authentication.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  } else {
    return <AuthLayout>{renderOTPScreen()}</AuthLayout>;
  }
};

export default OTP;
